import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Theme,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, useForm } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';

import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import Loader from '../../../components/Loader';
import FormSelectField from '../../../components/formFields/FormSelectField';
import FormTextField from '../../../components/formFields/FormTextField';
import FormDateField from '../../../components/formFields/FormDateField';
import { INTENT_TYPE_OPTIONS, PAYMENT_METHOD_OPTIONS } from '../../../constants/constants';
import { attorneyFeeEditPopupFormSchema } from '../../../helpers/formSchemas/formSchemas';
import { ATTORNEY_FEE_EDIT_POPUP_FORM_DEFAULT_VALUES } from '../../../helpers/formSchemas/formDefaultValues';
import { useEffect, useRef, useState } from 'react';
import {
    AttorneyFeeDisposition,
    AttorneyFeeEditPopupFormSubmitProps,
    UpdateAttorneyFeeDispositionRequest,
} from '../types';
import { getAttorneyFeeEntityIdentifier, updateFirmDisposition } from '../../../api/paymentsApi/settlementsApi';
import useSnackBar from '../../../hooks/useSnackBar';

interface EditAttorneyFeePopupProps {
    isOpen: boolean;
    loading?: boolean;
    onSuccess?: () => void;
    onFailed?: () => void;
    onCancel: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initialData?: any;
    settlementId: number;
    canEdit?: boolean;

}

const useStyles = makeStyles()((theme: Theme) => ({
    popupContainer: {
        '& .MuiDialog-paper': {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.light,
            maxWidth: '550px',
            width: '100%',
            padding: '32px',
            boxSizing: 'border-box',
            borderRadius: '16px',
            maxHeight: '95%',
        },

        '& .MuiDialogTitle-root': {
            padding: 0,
            fontSize: '34px',
            fontWeight: 600,
            lineHeight: '120%',
            color: theme.palette.common.white,
            fontFamily: 'Poppins',
        },

        '& .MuiDialogContent-root': {
            padding: 0,
            display: 'flex',
            justifyContent: 'center',
            '&::-webkit-scrollbar': {
                width: '5px',
                borderRadius: '20px',
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: 20,
                backgroundColor: theme.palette.secondary.dark,
            },
        },

        '& .MuiDialogContentText-root': {
            color: theme.palette.secondary.main,
        },

    },
    popupActions: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '416px',
        width: '100%',
        margin: '0 auto',
        gap: '16px',
        padding: 0,
    },
    popupButton: {
        color: theme.palette.info.main,

        '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.3)',
        },

        '&:hover': {
            background: 'rgba(144, 202, 249, 0.08)',
        },
    },
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '416px',
        width: '100%',
    },
    formFieldsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        alignItems: 'center',
        padding: '32px 0 32px 0',
    },
    checkboxWrapper: {
        width: '100%',

        '& label': {
            marginRight: 0,
        },
    },
}));


const EditAttorneyFeePopup = ({
    isOpen,
    loading,
    onCancel,
    initialData,
    settlementId,
    onSuccess,
    canEdit,

}: EditAttorneyFeePopupProps) => {
    const { classes } = useStyles();
    const { setAlert } = useSnackBar();
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const previousDispositionRef = useRef<string | null>(null);

    const handleClose = () => {
        reset({
            ...ATTORNEY_FEE_EDIT_POPUP_FORM_DEFAULT_VALUES,
            ...initialData,
        });
        onCancel();
    };

    const formMethods = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(attorneyFeeEditPopupFormSchema),
        defaultValues: ATTORNEY_FEE_EDIT_POPUP_FORM_DEFAULT_VALUES,
    });

    const {
        handleSubmit,
        watch,
        reset,
        setValue,
        formState: { isValid, isDirty },
    } = formMethods;

    // Watch disposition to determine if External is selected
    const disposition = watch('disposition');
    const isExternal = disposition === 'External';

    useEffect(() => {
        if (initialData) {
            reset({
                ...ATTORNEY_FEE_EDIT_POPUP_FORM_DEFAULT_VALUES,
                ...initialData,
                paymentMethod: initialData.paymentMethod !== '-'
                    ? initialData.paymentMethod
                    : null,
            });
        }
    }, [initialData, reset]);

    useEffect(() => {
        const previousDisposition = previousDispositionRef.current;

        if (
            previousDisposition &&
            previousDisposition !== 'External' &&
            disposition === 'External'
        ) {
            setValue('paymentMethod', null, { shouldDirty: true, shouldValidate: true });
            setValue('externalPaymentReference', null, { shouldDirty: true, shouldValidate: true });
            setValue('externalPaymentMemo', null, { shouldDirty: true, shouldValidate: true });
            setValue('externalPaymentDate', null, { shouldDirty: true, shouldValidate: true });
        }

        previousDispositionRef.current = disposition;
    }, [disposition, setValue]);

    const onSubmitForm = (values: AttorneyFeeEditPopupFormSubmitProps) => {
        setIsFormSubmitting(true);
        const entity = getAttorneyFeeEntityIdentifier(initialData ?? {});
        let payload: UpdateAttorneyFeeDispositionRequest;
        // Format externalPaymentDate as ISO string if present
        if (isExternal) {
            const formattedDate = values.externalPaymentDate instanceof Date
                ? values.externalPaymentDate.toISOString()
                : values.externalPaymentDate || null;
            payload = {
                ...entity,
                disposition: values.disposition as AttorneyFeeDisposition,
                externalPaymentMethod: values.paymentMethod || null,
                externalPaymentReference: values.externalPaymentReference === '' ? null : values.externalPaymentReference,
                externalPaymentMemo: values.externalPaymentMemo,
                externalPaymentDate: formattedDate,
            };
        } else {
            payload = {
                ...entity,
                disposition: values.disposition as AttorneyFeeDisposition,
            };
        }

        updateFirmDisposition(settlementId, payload)
            .then(() => {
                setIsFormSubmitting(false);
                setAlert((prev) => ({
                    ...prev,
                    message: 'Attorney fee details updated successfully',
                    type: 'success',
                }));
                onSuccess && onSuccess();
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .catch((err: any) => {
                setIsFormSubmitting(false);
                setAlert((prev) => ({
                    ...prev,
                    message: err?.response?.data?.message || 'Failed to update attorney fee details',
                    type: 'error',
                }));
            });
    };

    return (
        <Dialog className={classes.popupContainer} open={isOpen} onClose={handleClose} scroll="paper" >
            <Box display="flex" justifyContent="flex-end">
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    disableRipple
                    sx={(theme) => ({
                        color: theme.palette.common.white,
                    })}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogTitle>
                {'Edit Attorney Fee'}
            </DialogTitle>
            {loading ? (
                <Box sx={{ margin: '30px' }}>
                    <Loader colorType={'warning'} />
                </Box>) : (
                <>
                    <DialogContent>
                        <FormProvider {...formMethods}>
                            <Box className={classes.formContainer}>
                                <Box className={classes.formFieldsWrapper}>
                                    {/* 🟦 Deduction Section */}
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: '#fff',
                                            fontWeight: 600,
                                            alignSelf: 'flex-start',
                                            marginBottom: '8px',
                                            fontSize: '18px',
                                        }}
                                    >
                                        Payment
                                    </Typography>

                                    <FormSelectField
                                        name="disposition"
                                        label="Intent *"
                                        options={INTENT_TYPE_OPTIONS}
                                        width="100%"
                                        disabled={!canEdit}
                                    />

                                    {disposition === 'External' && (<>
                                        <FormSelectField
                                            name="paymentMethod"
                                            label={'Payment Method'}
                                            options={PAYMENT_METHOD_OPTIONS}
                                            width="100%"
                                            disabled={!canEdit}
                                            shouldClearable={true}
                                        />
                                        <FormTextField
                                            name="externalPaymentReference"
                                            label={'Payment Reference'}
                                            disabled={!canEdit}
                                        />
                                        <FormTextField
                                            name='externalPaymentMemo'
                                            label='Memo'
                                            isMultiline
                                            isDescription
                                            disabled={!canEdit}
                                        />
                                        <FormDateField
                                            name="externalPaymentDate"
                                            label={'Payment Date'}
                                            maxDate={moment()}
                                            disabled={!canEdit}
                                        />
                                    </>)}
                                </Box>

                                <DialogActions className={classes.popupActions}>
                                    {canEdit && (
                                        <>
                                            <FormSubmitButton
                                                disabled={!isDirty || !isValid || isFormSubmitting}
                                                buttonText="Submit"
                                                onClick={handleSubmit(onSubmitForm)}
                                            />
                                            <FormCancelButton onClick={handleClose} />
                                        </>
                                    )}
                                </DialogActions>
                            </Box>
                        </FormProvider>
                    </DialogContent>

                </>
            )}
        </Dialog>
    );
};

export default EditAttorneyFeePopup;